import React from 'react';
import PropTypes from 'prop-types';
import { defaultTemplate } from 'core/hoc';
import { BaasicModal, BaasicTable, TableFilter, EmptyState, BaasicButton, BasicQueryCheckbox, QueryDropdown, BasicQueryInput } from 'core/components';
import EmptyIcon from 'themes/assets/img/building-modern.svg';
import { ApplicationListLayout, Content, PageNavigation } from 'core/layouts';
import { isSome } from 'core/utils';
import { DropDownButton, DropDownButtonItem } from "@progress/kendo-react-buttons";
import { FacilityAdditionalContactSearchModal } from 'application/facility/components';

function NikoFacilityListTemplate({ nikoFacilityListViewStore }) {
    const {
        tableLoading,
        tableStore,
        queryUtility,
        sync,
        inactiveQueryCheckboxStore,
        onHoldQueryCheckboxStore,
        corporateStore,
        accountRepStore,
        downloadUrl,
        downloadESignUrl,
        routes,
        authorization,
        stateStore,
        addressStore,
        downloadRef,
        onDownloadFacilityReport,
        openFacilityAdditionalContactSearchModal,
        facilityAdditionalContactSearchModal,
        onDownloadESignReportModal
    } = nikoFacilityListViewStore;

	const handleDropDownItemsOnClick = (e) => {
        if (e.item.text === "Facility report") {
           onDownloadFacilityReport();
        }
        else if (e.item.text === "Contacts report") {
            openFacilityAdditionalContactSearchModal();
        }
        else if (e.item.text === "E-Sign report") {
            onDownloadESignReportModal();
        }
    }

    return (
        <ApplicationListLayout store={nikoFacilityListViewStore}>
            <PageNavigation>
                <BaasicButton authorized={authorization.create} className="btn btn--base btn--primary" label={'Sync'} icon="u-icon u-icon--sml u-icon--synchronize" onClick={sync} disabled={tableStore.loading}></BaasicButton>               
                <DropDownButton
					text="Download"
					onItemClick={(e) => handleDropDownItemsOnClick(e)}
                    ref={downloadRef}
                    buttonClass="btn btn--base btn--primary"
                    icon='u-icon u-icon--sml u-icon--download--white'
                    themeColor='primary'
				>
					{ downloadUrl && <DropDownButtonItem text="Facility report" />} 
					{ <DropDownButtonItem text="Contacts report" />} 
					{ downloadESignUrl && <DropDownButtonItem text="E-Sign report" />} 
				</DropDownButton>
                <BaasicButton authorized={authorization.update} className="btn btn--base btn--primary" label={'Upload'} icon='u-icon u-icon--sml u-icon--upload--white' onClick={() => routes.create()}></BaasicButton>               
            </PageNavigation>
            <Content emptyRenderer={renderEmpty()} >
                <div className='u-mar--bottom--sml'>
                    <TableFilter queryUtility={queryUtility}>
                        <div className="col col-sml-12 col-lrg-6 col-xxlrg-2 u-mar--bottom--sml">
                            <QueryDropdown queryUtility={queryUtility} store={corporateStore} propertyName="corporateId" label="Corporate"/>
                        </div>
                        <div className="col col-sml-12 col-lrg-6 col-xxlrg-2 u-mar--bottom--sml">
                            <QueryDropdown queryUtility={queryUtility} store={accountRepStore} propertyName="accountRepId" label="Account Representative"/>
                        </div>
                        <div className="col col-sml-12 col-lrg-6 col-xxlrg-2 u-mar--bottom--sml">
                            <QueryDropdown queryUtility={queryUtility} store={stateStore} propertyName="stateId" label="State"/>
                        </div>
                        <div className="col col-sml-12 col-lrg-6 col-xxlrg-2 u-mar--bottom--sml">
                            <BasicQueryInput 
                                queryUtility={queryUtility} 
                                store={addressStore}
                                type="search"
                                propertyName="address" 
                                label="Address" 
                            />
                        </div>
                        <div className="col col-sml-12 col-lrg-6 col-xxlrg-4 u-mar--bottom--sml u-flex--self--center">
                            <div className="u-flex--primary">
                                <div className="u-mar--right--med">
                                    <BasicQueryCheckbox id={'facilities'} queryUtility={queryUtility} propertyName={'inActive'} label={'Show Inactive'} store={inactiveQueryCheckboxStore}/>
                                </div>
                                <div>
                                    <BasicQueryCheckbox id={'onHold'} queryUtility={queryUtility} propertyName={'isOnHold'} label={'Show Only On Hold'} store={onHoldQueryCheckboxStore}/>
                                </div>
                            </div>
                        </div>
                    </TableFilter>
                </div>
                <div className="card--table">
                    <BaasicTable
                        tableStore={tableStore}
                        loading={tableLoading}
                        actionsComponent={renderActions}
                    />
                </div>

                <BaasicModal modalParams={facilityAdditionalContactSearchModal} className="modal__content modal--med">
                    <FacilityAdditionalContactSearchModal />
                </BaasicModal>
            </Content>
        </ApplicationListLayout>
    )
}

function renderEmpty() {
    return <EmptyState image={EmptyIcon} title='FACILITY.LIST.EMPTY_STATE.TITLE' description='FACILITY.LIST.EMPTY_STATE.DESCRIPTION' />
}

NikoFacilityListTemplate.propTypes = {
    nikoFacilityListViewStore: PropTypes.object
};

function renderActions({item, actions, authorization}){
    if (!isSome(actions)) return null;

    const { onEdit, onSchedule, onPreview } = actions;
    if (!isSome(onEdit) && !isSome(onPreview) && !isSome(onSchedule)) return null;

    return (
        <td className="table__body--data right">
            <div className="table__icons">
                {isSome(onPreview) ? (
                    <BaasicButton
                        authorization={authorization ? authorization.read : null}
                        className="btn btn--icon"
                        icon='u-icon u-icon--preview u-icon--sml'
                        label='FACILITY.LIST.BUTTON.PREVIEW'
                        onlyIcon={true}
                        onClick={() => onPreview(item)}>
                    </BaasicButton>
                ) : null}   
                {isSome(onEdit) ? (
                    <BaasicButton
                        authorization={authorization ? authorization.update : null}
                        className="btn btn--icon"
                        icon='u-icon u-icon--edit u-icon--sml'
                        label='FACILITY.LIST.BUTTON.EDIT'
                        onlyIcon={true}
                        onClick={() => onEdit(item)}>
                    </BaasicButton>
                ) : null}
                {isSome(onSchedule) ? (
                    <BaasicButton
                        authorization={authorization ? authorization.update : null}
                        className="btn btn--icon"
                        icon='u-icon u-icon--schedule u-icon--sml'
                        label='FACILITY.LIST.BUTTON.SCHEDULE'
                        onlyIcon={true}
                        onClick={() => onSchedule(item)}>
                    </BaasicButton>
                ) : null}
            </div>
        </td>
    );

}

renderActions.propTypes = {
    item: PropTypes.any,
    actions: PropTypes.any,
    authorization: PropTypes.any
};

export default defaultTemplate(NikoFacilityListTemplate);
